// The `blocks` structure symbol set: card 06 of mockup round 054,
// "ISOMETRIC BLOCKS" (R1103, R1104, `ui-map-interface.md` §6.2).
//
// Every object is a small 2.5D block construction, and each kind has its own
// footprint and height: the Throne is a stepped ziggurat, the Spire a thin
// tower with a mast, the Forge Vent a squat chimney cone, the Kelp Terrace
// three low stepped beds, the Deep Battery a bunker with a barrel, the
// Drydock Shoal an open cradle with a hull in it, the Archive Vault a closed
// cube with a door, the Marshal Court a wide hall with a colonnade, and the
// Coral Ward a domed rock.
//
// The holder's ink fills the two side faces. The contested tell is the roof:
// every top face takes the attacker's ink, and a pennant in the attacker's
// ink flies from the highest point. The card wags that pennant with the
// clock. The atlas bakes one still cell, so the pennant here stands at the
// middle of its wag (R1104).
//
// The card draws in an isometric frame and moves the context down per kind.
// `MarkGraphics` has no transform, so `pt()` carries the lift of the kind.
// The card's ellipses are sampled into point lists, because the interface has
// no ellipse. Every offset, height, radius and alpha below is the card's.

import type { MarkGraphics } from '../marks';
import {
  disc, ellipseArc, line, PALE, stroke, WATER,
  setOf, type StructureDraw, type StructureInk, type StructureKind,
  type StructureSet,
} from './shared';

/** The isometric projection of the card: 30 degrees. */
const C30 = Math.cos(Math.PI / 6);
const S30 = 0.5;

/** How far down the card moves the context of each kind. */
const LIFT: Record<StructureKind, number> = {
  throne: 4, spire: 12, forge: 6, kelp: 4, battery: 4,
  drydock: 2, archive: 8, marshal: 4, ward: 3,
};

/** A tint of one face: the ink and the fraction of the holder's alpha. */
interface Tint { ink: number; a: number }

/**
 * The rough rock of the Coral Ward, frozen.
 *
 * The card builds it from `mulberry32(9)`. The atlas bakes one still cell per
 * key, so the nine points are stated here rather than rolled again.
 */
const WARD_ROCK = [
  11.6, 3, 10.38, 7.79, 1.98, 9.19, -6.73, 9.41, -12.38, 5.48,
  -12.28, 0.54, -5.89, -2.61, 2.39, -4.45, 8.73, -1.03,
];

/** One kind of the `blocks` set, still. */
function markOf(kind: StructureKind): StructureDraw {
  const oy = LIFT[kind];
  return (g: MarkGraphics, ink: StructureInk): void => {
    const col = ink.own;
    const a = ink.held ? 1 : 0.72;
    const roof: Tint = ink.foe !== 0
      ? { ink: ink.foe, a: 0.8 }
      : { ink: col, a: 0.6 };

    /** One point of the isometric grid: a footprint cell at a height. */
    const pt = (u: number, v: number, h: number): [number, number] =>
      [(u - v) * C30, (u + v) * S30 - h + oy];

    /** A stroked segment in the frame of the kind. */
    const ln = (
      x0: number, y0: number, x1: number, y1: number,
      c: number, al: number, w: number,
    ): void => line(g, x0, y0 + oy, x1, y1 + oy, c, al, w);

    /**
     * One face: the water first, so a face behind it never shows through,
     * then the tint, then the edge in the holder's ink.
     */
    const face = (pts: number[], tint: Tint): void => {
      g.poly(pts).fill({ color: WATER, alpha: 0.92 });
      g.poly(pts).fill({ color: tint.ink, alpha: tint.a * a });
      g.poly(pts).stroke({ width: 1, color: col, alpha: 0.9 * a, join: 'round' });
    };

    /**
     * One block: the footprint `(u0, v0)` of size `(w, d)`, from height `h0`
     * to `h1`. The two front faces carry the holder's ink, the top face the
     * roof tint.
     */
    const box = (
      u0: number, v0: number, w: number, d: number, h0: number, h1: number,
    ): void => {
      const f0 = pt(u0, v0 + d, h0), f1 = pt(u0 + w, v0 + d, h0);
      const f2 = pt(u0 + w, v0 + d, h1), f3 = pt(u0, v0 + d, h1);
      face([...f0, ...f1, ...f2, ...f3], { ink: col, a: 0.24 });
      const s0 = pt(u0 + w, v0, h0), s1 = pt(u0 + w, v0, h1);
      face([...s0, ...f1, ...f2, ...s1], { ink: col, a: 0.42 });
      const t0 = pt(u0, v0, h1), t1 = pt(u0 + w, v0, h1);
      face([...t0, ...t1, ...f2, ...f3], roof);
    };

    // Where the pennant stands.
    let top: [number, number];
    switch (kind) {
      case 'throne':
        // Three steps of a ziggurat.
        box(-12, -12, 24, 24, 0, 5);
        box(-8, -8, 16, 16, 5, 10);
        box(-4, -4, 8, 8, 10, 16);
        top = pt(0, 0, 16);
        break;
      case 'spire': {
        // A wide plinth, a thin tower, and the mast with two yards.
        box(-7, -7, 14, 14, 0, 2);
        box(-3, -3, 6, 6, 2, 22);
        ln(0, -22, 0, -29, col, 0.9 * a, 1.2);
        ln(-4, -26, 4, -26, col, 0.9 * a, 1);
        ln(-2.5, -28.5, 2.5, -28.5, col, 0.9 * a, 1);
        top = [0, -29 + oy];
        break;
      }
      case 'forge': {
        // The chimney cone, its shaded right half, the base and the mouth.
        const cone = [-11, 0 + oy, -4, -14 + oy, 4, -14 + oy, 11, 0 + oy];
        face(cone, { ink: col, a: 0.24 });
        g.poly([0, 0 + oy, 0, -14 + oy, 4, -14 + oy, 11, 0 + oy])
          .fill({ color: col, alpha: 0.2 * a });
        const base: number[] = [];
        ellipseArc(base, 0, oy, 11, 5, 0, Math.PI, 16);
        g.poly(base, false);
        stroke(g, col, 0.9 * a, 1);
        const mouth: number[] = [];
        ellipseArc(mouth, 0, -14 + oy, 4, 1.8, 0, Math.PI * 2, 16);
        face(mouth, roof);
        top = [0, -15 + oy];
        break;
      }
      case 'kelp':
        // Three beds, each lower than the one behind it, with the stakes.
        box(-12, -12, 24, 8, 0, 8);
        box(-12, -4, 24, 8, 0, 5);
        box(-12, 4, 24, 8, 0, 2);
        for (const k of [[-6, -8, 8], [4, -8, 8], [-6, 0, 5],
          [4, 0, 5], [-6, 8, 2], [4, 8, 2]]) {
          const p = pt(k[0], k[1], k[2]);
          line(g, p[0], p[1], p[0], p[1] - 3, col, 0.9 * a, 1);
        }
        top = pt(-4, -8, 8);
        break;
      case 'battery': {
        // The bunker, the turret on it, and the barrel out to the right.
        box(-11, -8, 22, 16, 0, 7);
        box(-4, -4, 8, 8, 7, 11);
        const m0 = pt(0, 0, 11), m1 = pt(15, 0, 15);
        line(g, m0[0], m0[1], m1[0], m1[1], col, 0.95 * a, 3);
        g.circle(m1[0], m1[1], 1.8).stroke({ width: 1, color: col, alpha: 0.9 * a });
        top = pt(0, 0, 11);
        break;
      }
      case 'drydock': {
        // Two quays with the hull between them.
        box(-12, -9, 24, 4, 0, 5);
        const hull: number[] = [];
        for (let k = 0; k < 10; k++) {
          const q = pt(Math.cos(k * Math.PI / 5) * 9, Math.sin(k * Math.PI / 5) * 3, 1);
          hull.push(q[0], q[1]);
        }
        face(hull, { ink: col, a: 0.35 });
        box(-12, 5, 24, 4, 0, 5);
        top = pt(0, -7, 5);
        break;
      }
      case 'archive': {
        // The closed cube, with the door and its knob on the right face.
        box(-10, -10, 20, 20, 0, 17);
        const d0 = pt(10, 2, 0), d1 = pt(10, 8, 0);
        const d2 = pt(10, 8, 8), d3 = pt(10, 2, 8);
        const door = [...d0, ...d1, ...d2, ...d3];
        g.poly(door).fill({ color: WATER, alpha: 0.95 });
        g.poly(door).stroke({ width: 1, color: col, alpha: 0.9 * a, join: 'round' });
        const kn = pt(10, 3.2, 4);
        disc(g, kn[0], kn[1], 0.9, col, 0.9 * a);
        top = pt(0, 0, 17);
        break;
      }
      case 'marshal': {
        // The wide hall, with the colonnade along its front.
        box(-14, -7, 28, 14, 0, 9);
        for (let u = -11; u <= 11; u += 5.5) {
          const p0 = pt(u, 7, 0), p1 = pt(u, 7, 9);
          line(g, p0[0], p0[1], p1[0], p1[1], PALE, 0.55 * a, 1.2);
        }
        top = pt(0, 0, 9);
        break;
      }
      default: {
        // The Coral Ward: a rough rock under a dome, with the mending mast.
        const rock: number[] = [];
        for (let k = 0; k < WARD_ROCK.length; k += 2) {
          rock.push(WARD_ROCK[k], WARD_ROCK[k + 1] + oy);
        }
        face(rock, { ink: col, a: 0.28 });
        const dome: number[] = [];
        ellipseArc(dome, 0, oy, 8, 8, Math.PI, Math.PI * 2, 16);
        ellipseArc(dome, 0, oy, 8, 3.5, 0, Math.PI, 16);
        face(dome, roof);
        ln(0, -8, 0, -12, col, 0.9 * a, 1.2);
        ln(-2, -10, 2, -10, col, 0.9 * a, 1.2);
        top = [0, -12 + oy];
        break;
      }
    }

    if (ink.foe !== 0) {
      // The pennant of the attacker, at the middle of the wag the card gives
      // it, because the atlas bakes one still cell (R1104).
      line(g, top[0], top[1], top[0], top[1] - 7, PALE, 0.9, 1);
      g.poly([
        top[0], top[1] - 7,
        top[0] + 6, top[1] - 5.5,
        top[0], top[1] - 3.5,
      ]).fill({ color: ink.foe, alpha: 0.95 });
    }
  };
}

/** Card 06, all nine kinds. */
export const BLOCKS_SET: StructureSet = setOf(markOf);
